// Timezone constants
const TZ_ANCHORAGE = 'America/Anchorage';
const TZ_LOS_ANGELES = 'America/Los_Angeles';
const TZ_PHOENIX = 'America/Phoenix';
const TZ_DENVER = 'America/Denver';
const TZ_CHICAGO = 'America/Chicago';
const TZ_NEW_YORK = 'America/New_York';
const TZ_HONOLULU = 'Pacific/Honolulu';

// Current onboarded DCs.
export const DC_TIMEZONE_MAP: Record<string, string> = {
    WANC: TZ_ANCHORAGE,
    WAUB: TZ_LOS_ANGELES,
    WBRE: TZ_LOS_ANGELES,
    WDAL: TZ_CHICAGO,
    WDEN: TZ_DENVER,
    WIRV: TZ_LOS_ANGELES,
    WLAN: TZ_NEW_YORK,
    WLAS: TZ_LOS_ANGELES,
    WLLA: TZ_CHICAGO,
    WMEL: TZ_CHICAGO,
    WMET: TZ_NEW_YORK,
    WNCA: TZ_LOS_ANGELES,
    WPHA: TZ_PHOENIX,
    WPOA: TZ_LOS_ANGELES,
    WPON: TZ_CHICAGO,
    WRCT: TZ_CHICAGO,
    WSLC: TZ_DENVER,
    WSPK: TZ_LOS_ANGELES,
    WSUN: TZ_NEW_YORK,
    WWEL: TZ_NEW_YORK,
    WHAW: TZ_HONOLULU
};

const normalizeDc = (dcName?: string): string => (dcName || '').trim().toUpperCase();

export const getDcTimeZone = (dcName?: string): string => {
    const normalizedDc = normalizeDc(dcName);
    if (!normalizedDc) {
        throw new Error('No DC found');
    }

    const timeZone = DC_TIMEZONE_MAP[normalizedDc];
    if (!timeZone) {
        throw new Error(`No timezone found for DC: ${normalizedDc}`);
    }

    return timeZone;
};

const formatDateToYyyyMmDd = (date: Date, timeZone: string): string => {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });

    const parts = formatter.formatToParts(date);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;

    if (!year || !month || !day) {
        const fallback = new Date();
        const fallbackYear = fallback.getFullYear();
        const fallbackMonth = String(fallback.getMonth() + 1).padStart(2, '0');
        const fallbackDay = String(fallback.getDate()).padStart(2, '0');
        return `${fallbackYear}-${fallbackMonth}-${fallbackDay}`;
    }

    return `${year}-${month}-${day}`;
};

const formatDateTimeToYyyyMmDdHhMmSs = (date: Date, timeZone: string): string => {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });

    const parts = formatter.formatToParts(date);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;
    const hour = parts.find((part) => part.type === 'hour')?.value;
    const minute = parts.find((part) => part.type === 'minute')?.value;
    const second = parts.find((part) => part.type === 'second')?.value;

    if (!year || !month || !day || !hour || !minute || !second) {
        const fallback = new Date();
        const fallbackYear = fallback.getFullYear();
        const fallbackMonth = String(fallback.getMonth() + 1).padStart(2, '0');
        const fallbackDay = String(fallback.getDate()).padStart(2, '0');
        const fallbackHour = String(fallback.getHours()).padStart(2, '0');
        const fallbackMinute = String(fallback.getMinutes()).padStart(2, '0');
        const fallbackSecond = String(fallback.getSeconds()).padStart(2, '0');
        return `${fallbackYear}-${fallbackMonth}-${fallbackDay} ${fallbackHour}:${fallbackMinute}:${fallbackSecond}`;
    }

    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
};

export const getCurrentDateForDC = (
    dcName?: string,
    now: Date = new Date()
): string => {
    const timeZone = getDcTimeZone(dcName);
    return formatDateTimeToYyyyMmDdHhMmSs(now, timeZone);
};

export const getCurrentDateOnlyForDC = (
    dcName?: string,
    now: Date = new Date()
): string => {
    const timeZone = getDcTimeZone(dcName);
    return formatDateToYyyyMmDd(now, timeZone);
};
